import { useChangePassword } from "@/api/mutations/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ChangePasswordForm,
  type ChangePasswordFormData,
} from "@/pages/profile/components/change-password/change-password-form";
import type { AxiosError } from "axios";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const ChangePassword = () => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useChangePassword();

  const onSubmit = async (values: ChangePasswordFormData) => {
    mutateAsync(values)
      .then(() => {
        toast.success("Password updated successfully", {
          description:
            "Your password has been changed. You can now use your new password to log in.",
        });
        setOpen(false);
      })
      .catch((error: AxiosError<{ message: string }, unknown>) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || "Unable to update password";

        toast.error("Password update failed", {
          description:
            errorMessage === "Unable to update password"
              ? "Please check your current password and try again."
              : errorMessage,
        });
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          data-testid="change-password-trigger"
          variant="outline"
          className="cursor-pointer flex items-center gap-2"
        >
          <KeyRound className="h-4 w-4" />
          Change Password
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Enter your current password and choose a new password.
          </DialogDescription>
        </DialogHeader>
        <div>
          <ChangePasswordForm onSubmit={onSubmit} isPending={isPending} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
